import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth } from "./AuthContext";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_55a9f4cb-4c33-47bd-86a7-80fe678bbbdb/artifacts/wavtipa0_PROKSY%20RESEARCH%20%282%29.png";

export default function PortalLogin() {
    const { user, login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (user) navigate("/portal", { replace: true });
    }, [user, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        if (loading) return;
        setLoading(true);
        setErr("");
        try {
            await login(email.trim(), password);
            navigate("/portal", { replace: true });
        } catch (e) {
            setErr(e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center px-4 py-16 bg-[#050B1A] relative overflow-hidden"
            data-testid="portal-login-page"
        >
            <div className="absolute inset-0 bg-grid bg-grid-fade opacity-40 pointer-events-none" />
            <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-blue-700/15 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" />

            <motion.div
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55 }}
                className="w-full max-w-md relative"
            >
                <Link
                    to="/"
                    className="mb-7 flex items-center justify-center gap-3"
                    data-testid="login-logo-link"
                >
                    <div className="w-12 h-12 rounded-xl overflow-hidden border border-white/10 bg-black/30">
                        <img
                            src={LOGO_URL}
                            alt="Proksy"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="leading-tight">
                        <p className="font-outfit font-bold text-white">
                            PROKSY
                        </p>
                        <p className="font-outfit text-[10px] tracking-[0.32em] text-blue-400">
                            EMPLOYEE PORTAL
                        </p>
                    </div>
                </Link>

                <div className="pr-glass-strong rounded-3xl p-7 sm:p-8">
                    <h1 className="font-outfit text-2xl font-bold text-white tracking-tight">
                        Sign in to your inbox
                    </h1>
                    <p className="mt-1.5 text-sm text-slate-400">
                        Pre-invited employees only.
                    </p>

                    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
                        <div>
                            <label className="block font-outfit text-xs uppercase tracking-[0.22em] text-blue-300 mb-2">
                                Email
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoFocus
                                    required
                                    autoComplete="email"
                                    placeholder="you@proksyresearch.com"
                                    className="input-glow w-full rounded-xl py-3 pl-10 pr-4 text-sm text-white placeholder:text-slate-500"
                                    data-testid="login-email"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block font-outfit text-xs uppercase tracking-[0.22em] text-blue-300 mb-2">
                                Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                    placeholder="Your account password"
                                    className="input-glow w-full rounded-xl py-3 pl-10 pr-4 text-sm text-white placeholder:text-slate-500"
                                    data-testid="login-password"
                                />
                            </div>
                        </div>

                        {err && (
                            <div
                                className="text-xs text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded-lg px-3 py-2"
                                data-testid="login-error"
                            >
                                {err}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary w-full justify-center disabled:opacity-50"
                            data-testid="login-submit"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <ArrowRight className="w-4 h-4" />
                            )}
                            Sign in
                        </button>
                    </form>

                    <div className="mt-6 flex items-center gap-2 text-[11px] font-mono-pr text-slate-500">
                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                        Encrypted credentials. Brute-force protected.
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
